/**
 * review_code Tool
 * Comprehensive code review system for SmartStack projects
 *
 * Analyzes code for:
 * - Security vulnerabilities (OWASP)
 * - Architecture violations (Clean Architecture layers)
 * - Hardcoded values (magic numbers, strings)
 * - Test coverage
 * - AI-generated code issues (hallucinations)
 * - Performance anti-patterns (N+1, over-fetching)
 * - Dead code (unused imports, functions)
 * - Internationalization (i18n)
 * - Accessibility (a11y)
 */

import { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { Config } from '../types/index.js';
import { ReviewCodeInputSchema } from '../types/index.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { findFiles, readText } from '../utils/fs.js';
import { getChangedFiles, getStagedFiles } from '../utils/git.js';
import { logger } from '../lib/logger.js';

import type {
  CodeReviewResult,
  CodeReviewFinding,
  CheckContext,
  FileInfo,
  ReviewCategory,
} from './review-code/types.js';
import { resetFindingCounters } from './review-code/types.js';

import { checkSecurity } from './review-code/checks/security.js';
import { checkArchitecture } from './review-code/checks/architecture.js';
import { checkHardcodedValues } from './review-code/checks/hardcoded-values.js';
import { checkTests } from './review-code/checks/tests.js';
import { checkAiHallucinations } from './review-code/checks/ai-hallucinations.js';
import { checkPerformance } from './review-code/checks/performance.js';
import { checkDeadCode } from './review-code/checks/dead-code.js';
import { checkI18n } from './review-code/checks/i18n.js';
import { checkAccessibility } from './review-code/checks/accessibility.js';
import { formatMarkdownReport } from './review-code/formatters/markdown.js';

// Tool Definition
export const reviewCodeTool: Tool = {
  name: 'review_code',
  description: `Comprehensive code review tool for SmartStack projects.

Analyzes code for 9 categories of issues:
- **security**: OWASP vulnerabilities, hardcoded secrets, SQL injection, XSS
- **architecture**: Clean Architecture layer violations
- **hardcoded-values**: Magic numbers, hardcoded URLs, feature flags
- **tests**: Missing tests, test quality issues
- **ai-hallucinations**: Non-existent imports, phantom methods
- **performance**: N+1 queries, over-fetching, missing memoization
- **dead-code**: Unused imports, functions, variables
- **i18n**: Non-internationalized UI text
- **accessibility**: Missing alt text, ARIA issues, focus indicators

Returns a markdown report with findings grouped by severity (blocking, critical, warning, info).`,
  inputSchema: {
    type: 'object',
    properties: {
      path: {
        type: 'string',
        description: 'Project path to review (default: SmartStack.app path)',
      },
      scope: {
        type: 'string',
        enum: ['all', 'changed', 'staged'],
        default: 'all',
        description: 'Files to review: all, only changed (git diff), or staged files',
      },
      checks: {
        type: 'array',
        items: {
          type: 'string',
          enum: [
            'security',
            'architecture',
            'hardcoded-values',
            'tests',
            'ai-hallucinations',
            'performance',
            'dead-code',
            'i18n',
            'accessibility',
            'all',
          ],
        },
        default: ['all'],
        description: 'Categories of checks to run',
      },
      severity: {
        type: 'string',
        enum: ['blocking', 'critical', 'warning', 'all'],
        default: 'all',
        description: 'Filter results by minimum severity',
      },
    },
  },
};

// Handler
export async function handleReviewCode(args: unknown, config: Config): Promise<string> {
  const startTime = Date.now();

  // Parse and validate input
  const input = ReviewCodeInputSchema.parse(args);
  const projectPath = input.path || config.smartstack.projectPath;

  logger.info('Starting code review', { projectPath, scope: input.scope, checks: input.checks });

  // Reset finding ID counters
  resetFindingCounters();

  // Detect project structure
  const structure = await findSmartStackStructure(projectPath);
  if (!structure) {
    return '# Code Review Error\n\nCould not detect SmartStack project structure at the specified path.';
  }

  // Get files to analyze
  const files = await getFilesToAnalyze(projectPath, input.scope, structure);

  if (files.length === 0) {
    return '# Code Review Report\n\nNo files to analyze for the specified scope.';
  }

  logger.info(`Analyzing ${files.length} files`);

  // Build check context
  const context: CheckContext = {
    projectPath,
    files,
    scope: input.scope,
  };

  // Determine which checks to run
  const checksToRun = input.checks.includes('all')
    ? ['security', 'architecture', 'hardcoded-values', 'tests', 'ai-hallucinations', 'performance', 'dead-code', 'i18n', 'accessibility']
    : input.checks;

  // Run checks in parallel
  const checkFunctions: Record<string, (ctx: CheckContext) => Promise<CodeReviewFinding[]>> = {
    security: checkSecurity,
    architecture: checkArchitecture,
    'hardcoded-values': checkHardcodedValues,
    tests: checkTests,
    'ai-hallucinations': checkAiHallucinations,
    performance: checkPerformance,
    'dead-code': checkDeadCode,
    i18n: checkI18n,
    accessibility: checkAccessibility,
  };

  const checkPromises = checksToRun
    .filter((check) => checkFunctions[check])
    .map(async (check) => {
      try {
        logger.debug(`Running ${check} check`);
        return await checkFunctions[check](context);
      } catch (error) {
        logger.error(`Error in ${check} check`, { error });
        return [];
      }
    });

  const checkResults = await Promise.all(checkPromises);
  const allFindings = checkResults.flat();

  // Filter by severity if specified
  const filteredFindings = filterBySeverity(allFindings, input.severity);

  // Calculate stats
  const stats = calculateStats(filteredFindings, files, startTime);

  // Calculate summary
  const summary = calculateSummary(filteredFindings, stats);

  // Build result
  const result: CodeReviewResult = {
    summary,
    findings: filteredFindings,
    stats,
  };

  logger.info('Code review complete', {
    filesScanned: stats.filesScanned,
    issuesFound: filteredFindings.length,
    duration: stats.duration,
  });

  // Format and return
  return formatMarkdownReport(result);
}

// Helper Functions

async function getFilesToAnalyze(
  projectPath: string,
  scope: 'all' | 'changed' | 'staged',
  _structure: Awaited<ReturnType<typeof findSmartStackStructure>>
): Promise<FileInfo[]> {
  let filePaths: string[] = [];

  if (scope === 'changed') {
    filePaths = await getChangedFiles(projectPath);
  } else if (scope === 'staged') {
    filePaths = await getStagedFiles(projectPath);
  } else {
    // Get all source files
    const patterns = ['**/*.cs', '**/*.ts', '**/*.tsx', '**/*.js', '**/*.jsx'];
    const excludePatterns = ['**/bin/**', '**/obj/**', '**/node_modules/**', '**/dist/**', '**/*.d.ts', '**/Migrations/**'];

    for (const pattern of patterns) {
      const found = await findFiles(pattern, {
        cwd: projectPath,
        ignore: excludePatterns,
      });
      filePaths.push(...found);
    }
  }

  // Read file contents and build FileInfo objects
  const files: FileInfo[] = [];

  for (const filePath of filePaths) {
    try {
      const fullPath = filePath.startsWith(projectPath)
        ? filePath
        : `${projectPath}/${filePath}`;

      const content = await readText(fullPath);
      const relativePath = filePath.replace(projectPath, '').replace(/^[/\\]/, '');

      files.push({
        path: fullPath,
        relativePath,
        content,
        language: detectLanguage(filePath),
        layer: detectLayer(relativePath),
        lineCount: content.split('\n').length,
      });
    } catch {
      // Skip files that can't be read
      logger.debug(`Could not read file: ${filePath}`);
    }
  }

  return files;
}

function detectLanguage(filePath: string): FileInfo['language'] {
  const ext = filePath.toLowerCase().split('.').pop();
  switch (ext) {
    case 'cs':
      return 'csharp';
    case 'ts':
      return 'typescript';
    case 'tsx':
      return 'tsx';
    case 'js':
      return 'javascript';
    case 'jsx':
      return 'jsx';
    case 'css':
      return 'css';
    case 'json':
      return 'json';
    default:
      return 'other';
  }
}

function detectLayer(relativePath: string): FileInfo['layer'] {
  const lower = relativePath.toLowerCase();
  if (lower.includes('/domain/') || lower.includes('\\domain\\')) return 'domain';
  if (lower.includes('/application/') || lower.includes('\\application\\')) return 'application';
  if (lower.includes('/infrastructure/') || lower.includes('\\infrastructure\\')) return 'infrastructure';
  if (lower.includes('/api/') || lower.includes('\\api\\')) return 'api';
  if (lower.includes('/web/') || lower.includes('\\web\\') || lower.includes('/src/')) return 'web';
  return undefined;
}

function filterBySeverity(
  findings: CodeReviewFinding[],
  minSeverity: 'blocking' | 'critical' | 'warning' | 'all'
): CodeReviewFinding[] {
  if (minSeverity === 'all') return findings;

  const severityOrder = ['blocking', 'critical', 'warning', 'info'];
  const minIndex = severityOrder.indexOf(minSeverity);

  return findings.filter((f) => severityOrder.indexOf(f.severity) <= minIndex);
}

function calculateStats(
  findings: CodeReviewFinding[],
  files: FileInfo[],
  startTime: number
): CodeReviewResult['stats'] {
  const byCategory: Record<ReviewCategory, number> = {
    security: 0,
    architecture: 0,
    'hardcoded-values': 0,
    tests: 0,
    'ai-hallucinations': 0,
    performance: 0,
    'dead-code': 0,
    i18n: 0,
    accessibility: 0,
  };

  const findingsCount = {
    blocking: 0,
    critical: 0,
    warning: 0,
    info: 0,
  };

  for (const finding of findings) {
    findingsCount[finding.severity]++;
    byCategory[finding.category]++;
  }

  return {
    filesScanned: files.length,
    linesScanned: files.reduce((sum, f) => sum + f.lineCount, 0),
    duration: Date.now() - startTime,
    findingsCount,
    byCategory,
  };
}

function calculateSummary(
  _findings: CodeReviewFinding[],
  stats: CodeReviewResult['stats']
): CodeReviewResult['summary'] {
  // Calculate score (100 - penalties)
  let score = 100;
  score -= stats.findingsCount.blocking * 20; // -20 per blocking
  score -= stats.findingsCount.critical * 5;  // -5 per critical
  score -= stats.findingsCount.warning * 1;   // -1 per warning
  score = Math.max(0, Math.min(100, score));

  // Determine grade
  let grade: 'A' | 'B' | 'C' | 'D' | 'F';
  if (score >= 90) grade = 'A';
  else if (score >= 80) grade = 'B';
  else if (score >= 70) grade = 'C';
  else if (score >= 60) grade = 'D';
  else grade = 'F';

  // Determine status
  let status: 'passed' | 'failed' | 'warning';
  if (stats.findingsCount.blocking > 0) {
    status = 'failed';
  } else if (stats.findingsCount.critical > 0) {
    status = 'warning';
  } else {
    status = 'passed';
  }

  // Build message
  let message: string;
  if (status === 'failed') {
    message = `Review failed with ${stats.findingsCount.blocking} blocking issue(s). These must be fixed before merging.`;
  } else if (status === 'warning') {
    message = `Review passed with warnings. ${stats.findingsCount.critical} critical issue(s) should be addressed.`;
  } else {
    message = 'Code review passed. Great job!';
  }

  return { status, score, grade, message };
}
